import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CommOperation, OperationState } from 'app/shared/models';
import { Error } from 'app/shared/models/response-base';
import { DxFileUploaderComponent } from 'devextreme-angular/ui/file-uploader';
import { DxPopupComponent } from 'devextreme-angular';
import { firstValueFrom } from 'rxjs';
import { AnalyticsService } from 'app/shared/analytics/services/analytics.service';
import { MembershipLevelService } from '../../services/membership-level.service';

@Component({
  selector: 'sp-membership-level-import-modal',
  standalone: false,
  templateUrl: './membership-level-import-modal.component.html',
  styleUrl: './membership-level-import-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MembershipLevelImportModalComponent {
  @ViewChild('importPopup', { static: true }) importPopup: DxPopupComponent;
  @ViewChild('dxFileUploader', { static: false }) dxFileUploader: DxFileUploaderComponent;
  @ViewChild('importDropZone', { static: false }) importDropZone: ElementRef<HTMLElement>;
  @ViewChild('importErrorsPanel', { static: false }) importErrorsPanel: ElementRef<HTMLElement>;

  @Output() onUploaded = new EventEmitter<CommOperation>();

  /** Current property (workspace selection). */
  propertyId: string;

  formGroup: FormGroup;
  csvTemplateUrl = 'assets/documents/membership-levels-import-template.csv';
  dxFileUploadValue: File[] = [];
  fileName = '';
  hasQueuedFileUpload = false;
  uploadInProgress = false;
  importErrors: Error[] = [];
  /** When multiple row/file issues exist, summary is visible first; details expand on demand. */
  importErrorsExpanded = false;
  /** Visual highlight while dragging over the drop zone (DevExtreme file uploader). */
  isDropZoneActive = false;

  closePopup = () => {
    this.importPopup?.instance?.hide();
  };

  dxUpload = () => {
    const file = this.dxFileUploadValue?.[0];
    if (!this.propertyId || !file) {
      return;
    }
    void this.uploadImportFile(file);
  };

  constructor(
    private readonly membershipApi: MembershipLevelService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly analytics: AnalyticsService,
  ) {
    this.formGroup = this.fb.group({});
  }

  openModal(): void {
    this.importPopup?.instance?.show();
  }

  onPopupShown(): void {
    this.dxFileUploadValue = [];
    this.fileName = '';
    this.hasQueuedFileUpload = false;
    this.uploadInProgress = false;
    this.importErrors = [];
    this.importErrorsExpanded = false;
    this.isDropZoneActive = false;
    this.dxFileUploader?.instance?.reset();
    this.cdr.markForCheck();
  }

  toggleImportErrorsExpanded(): void {
    this.importErrorsExpanded = !this.importErrorsExpanded;
    this.cdr.markForCheck();
  }

  onDxValueChanged(event: { value: File[] }): void {
    const file = event.value?.[0];
    if (file) {
      this.fileName = file.name;
      this.hasQueuedFileUpload = true;
      this.importErrors = [];
      this.importErrorsExpanded = false;
      this.cdr.markForCheck();
      return;
    }

    // User cleared the selection (or canceled the dialog after previously selecting a file).
    this.dxFileUploadValue = [];
    this.fileName = '';
    this.hasQueuedFileUpload = false;
    this.importErrors = [];
    this.importErrorsExpanded = false;
    this.cdr.markForCheck();
  }

  onDropZoneEnter(): void {
    this.isDropZoneActive = true;
    this.cdr.markForCheck();
  }

  onDropZoneLeave(): void {
    this.isDropZoneActive = false;
    this.cdr.markForCheck();
  }

  /** Keyboard: same as clicking the drop zone (opens file picker via DevExtreme `dialogTrigger`). */
  triggerBrowseClick(): void {
    this.importDropZone?.nativeElement?.click();
  }

  private static readonly IMPORT_FAILED_MESSAGE = 'Import failed. Please try again.';

  private async uploadImportFile(file: File): Promise<void> {
    this.uploadInProgress = true;
    this.cdr.markForCheck();
    try {
      this.analytics.membershipLevelsCsvImportUsedEvent();
      const result = await firstValueFrom(this.membershipApi.importCsvFile(this.propertyId, file));
      this.uploadInProgress = false;
      const mappedErrors = result.errors.map(e => ({
        code: 0,
        source: e.row > 0 ? `Row ${e.row}` : '',
        details: e.message,
      }));

      // All-or-nothing: any row errors means nothing was imported.
      if (result.errors.length > 0) {
        this.analytics.membershipLevelsCsvImportErrorsEncounteredEvent(result.errors.length);
        this.importErrors = mappedErrors;
        // Show details immediately so users don't miss them.
        this.importErrorsExpanded = true;
        this.cdr.markForCheck();
        this.onUploaded.emit(
          Object.assign(new CommOperation(), {
            operationState: OperationState.failed,
            errors: this.importErrors,
            importedCount: 0,
          }),
        );
        return;
      }

      this.onUploaded.emit(
        Object.assign(new CommOperation(), {
          operationState: OperationState.success,
          errors: [],
          importedCount: result.importedCount,
        }),
      );

      // Track membership levels created via import (at least once per successful import operation).
      this.analytics.membershipLevelCreatedEvent('Import');

      this.resetUploadState();
      this.importErrors = [];
      this.cdr.markForCheck();
      this.importPopup?.instance?.hide();
    } catch (err: unknown) {
      this.uploadInProgress = false;
      const custom = err as { importErrors?: { row: number; message: string }[] };
      if (custom?.importErrors?.length) {
        this.analytics.membershipLevelsCsvImportErrorsEncounteredEvent(custom.importErrors.length);
        this.importErrors = custom.importErrors.map(e => ({
          code: 0,
          source: e.row > 0 ? `Row ${e.row}` : '',
          details: e.message,
        }));
        this.importErrorsExpanded = true;
      } else {
        this.importErrors = [{ code: 0, source: '', details: MembershipLevelImportModalComponent.IMPORT_FAILED_MESSAGE }];
        this.importErrorsExpanded = true;
      }
      this.cdr.markForCheck();
      this.onUploaded.emit(
        Object.assign(new CommOperation(), { operationState: OperationState.failed, errors: this.importErrors }),
      );
    }
  }

  private resetUploadState(): void {
    this.uploadInProgress = false;
    this.dxFileUploadValue = [];
    this.fileName = '';
    this.hasQueuedFileUpload = false;
  }
}
